import React from 'react';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {Totals, TotalsDataHook} from '../../../../common/components/Totals/Totals';
import {Text, TextPriority} from '@wix/wix-ui-tpa/cssVars';
import {useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {classes} from './PaymentRequestTotals.st.css';
import {useControllerProps} from '../../Widget/ControllerContext';
import {SPECS} from '../../../../common/constants';

export enum PaymentRequestTotalsDataHooks {
  Section = 'PaymentRequestTotalsDataHooks.section',
  TotalsSectionWrapper = 'PaymentRequestTotalsDataHooks.totalsSectionWrapper',
  TotalSection = 'PaymentRequestTotalsDataHooks.totalSection',
  TotalRow = 'PaymentRequestTotalsDataHooks.totalRow',
  Price = 'PaymentRequestTotalsDataHooks.price',
  ExpirationTextRow = 'PaymentRequestTotalsDataHooks.expirationText',
  SubscriptionInfoRow = 'PaymentRequestTotalsDataHooks.subscriptionInfoRow',
}

export interface PaymentRequestTotalsProps {
  total: string;
}

export const PaymentRequestTotals = ({total}: PaymentRequestTotalsProps) => {
  const {experiments} = useExperiments();
  const localeKeys = useLocaleKeys();
  const {
    paymentRequestStore: {paymentRequest},
  } = useControllerProps();

  const shouldShowExpirationDate = Boolean(paymentRequest.expirationDate);
  const showSubscriptionInfo = experiments.enabled(SPECS.SupportSubscriptionDescriptionOnPaymentRequest);

  const getSubscriptionInfoTexts = (): string[] =>
    paymentRequest.subscriptionInfo.flatMap(({autoRenewal, billingCycles}) => {
      const texts: string[] = [];
      if (autoRenewal) {
        texts.push(localeKeys.checkout.payLinks.recurringPayment.autorenew());
      }
      if (billingCycles) {
        texts.push(localeKeys.checkout.payLinks.recurringPayment.billingCycle({billing_cycles: billingCycles}));
      }
      return texts;
    });

  return (
    <section
      className={classes.totalsContainer}
      data-hook={PaymentRequestTotalsDataHooks.Section}
      aria-label={localeKeys.checkout.orderTotal.fullSection.ariaLabel()}>
      <Totals dataHook={PaymentRequestTotalsDataHooks.TotalsSectionWrapper}>
        <Totals.TotalsSection dataHook={PaymentRequestTotalsDataHooks.TotalSection}>
          <Totals.TotalsRow
            dataHook={PaymentRequestTotalsDataHooks.TotalRow}
            title={() => (
              <Text className={classes.totalRowText} data-hook={TotalsDataHook.TotalsRowTitle}>
                {localeKeys.checkout.order_summary.totals.total()}
              </Text>
            )}
            value={() => (
              <Text data-hook={PaymentRequestTotalsDataHooks.Price} className={classes.totalRowText}>
                {total}
              </Text>
            )}
          />
          {showSubscriptionInfo && (
            <>
              {getSubscriptionInfoTexts().map((text, index) => (
                <Totals.TotalsRow
                  key={index}
                  dataHook={PaymentRequestTotalsDataHooks.SubscriptionInfoRow}
                  title={() => (
                    <div>
                      <Text className={classes.subscriptionInfoText} priority={TextPriority.secondary}>
                        {text}
                      </Text>
                    </div>
                  )}
                />
              ))}
            </>
          )}
          {shouldShowExpirationDate && (
            <Totals.TotalsRow
              dataHook={PaymentRequestTotalsDataHooks.ExpirationTextRow}
              title={() => (
                <Text
                  data-hook={TotalsDataHook.TotalsRowTitle}
                  className={classes.totalRowExpirationText}
                  priority={TextPriority.secondary}>
                  {localeKeys.checkout.payLinks.paylinkValid.disclaimer({
                    expirationDate: paymentRequest.expirationDate,
                  })}
                </Text>
              )}
            />
          )}
        </Totals.TotalsSection>
      </Totals>
    </section>
  );
};
